'use client';

import { motion } from 'framer-motion';

type SplitTextTag = 'div' | 'h1' | 'h2' | 'h3' | 'p';

interface SplitTextProps {
    text: string;
    className?: string;
    delay?: number;
    /**
     * Element to render. Defaults to `div` so existing callers are unchanged.
     *
     * Pass `h1` on page templates that rely on this for their main heading — the
     * service and industry detail pages did, which left them with no `<h1>` at all.
     * The per-word `<span>` split also fragments the phrase for anything reading the
     * DOM, so `aria-label` below restores it as one string.
     */
    as?: SplitTextTag;
}

export default function SplitText({ text, className = '', delay = 0, as = 'div' }: SplitTextProps) {
    const words = text.split(' ');
    const MotionTag = motion[as];

    const container = {
        hidden: { opacity: 0 },
        visible: (i = 1) => ({
            opacity: 1,
            transition: { staggerChildren: 0.08, delayChildren: delay * i },
        }),
    };

    const child = {
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                type: 'spring',
                damping: 20,
                stiffness: 100,
            },
        },
        hidden: {
            opacity: 0,
            y: 20,
        },
    };

    return (
        <MotionTag
            className={`flex flex-wrap ${className}`}
            variants={container}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            aria-label={text}
        >
            {words.map((word, index) => (
                <motion.span
                    variants={child}
                    key={index}
                    className="mr-[0.25em] mb-2 inline-block"
                    aria-hidden="true"
                >
                    {/* The trailing space matters. These spans are flex items, so the
                        gap between words is drawn by `mr` and there is no whitespace
                        anywhere in the markup — meaning anything that extracts text by
                        stripping tags reads "RestaurantMarketingAgencyinRiyadh" as a
                        single token. Screen readers were already covered by the
                        `aria-label` above, but crawlers and LLMs are not, and headings
                        are the highest-weighted text on the page.

                        Inside the span it is collapsed by normal white-space handling
                        and never rendered, so spacing is visually identical. */}
                    {index < words.length - 1 ? `${word} ` : word}
                </motion.span>
            ))}
        </MotionTag>
    );
}
